/**
 * * Project Name : Driev
 * * Layer Name : Database
 * * Section : BannerSliders Form
 * * Parent : Admin
 * Description : design BannerSliders form primary admin with Database
 * Author: Suryakanta sahu
 * Date Created: 31-May-2022
 *
 */
/** --------------------- Primary Import Declaration ------------- */
import React, { useState, useRef, useEffect } from "react";
/** --------------------- MUI Components & APIs ------------------ */
import {
  Box,
  Button,
  IconButton,
  Typography,
  Alert,
  Fade,
  Paper,
} from "@mui/material";
/** ------------------ User Components ------------------- */
import { ThemeContext } from "context/ThemeContext";
/** ------------------ Icons ----------------------------- */
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export default function ImageUploader(props) {
  const { imageArray = [], setImageArray, multiple = false } = props;

  // Refs
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // State
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);

  /** ------------------ useContext ------------------------- */
  const themeMode = React.useContext(ThemeContext);
  const darkMode = themeMode.state.darkMode;

  // Allowed file types
  const fileExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".tiff",
    ".mp3",
    ".mp4",
    ".wav",
    ".m4a",
  ];

  // Max file size (20MB)
  const MAX_FILE_SIZE = 20971520;

  // Accept string for file input
  const acceptString = fileExtensions.join(",");

  // Set up drag event listeners
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(true);
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    };

    dropArea.addEventListener("dragover", handleDragOver);
    dropArea.addEventListener("dragenter", handleDragEnter);
    dropArea.addEventListener("dragleave", handleDragLeave);
    dropArea.addEventListener("drop", handleDrop);

    return () => {
      dropArea.removeEventListener("dragover", handleDragOver);
      dropArea.removeEventListener("dragenter", handleDragEnter);
      dropArea.removeEventListener("dragleave", handleDragLeave);
      dropArea.removeEventListener("drop", handleDrop);
    };
  }, []);

  // Function to check if file type is valid
  const isValidFileType = (file) => {
    const extension = "." + file.name.split(".").pop().toLowerCase();
    return fileExtensions.includes(extension);
  };

  // Function to check if file size is valid
  const isValidFileSize = (file) => {
    return file.size <= MAX_FILE_SIZE;
  };

  // Function to determine file type
  const getFileType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("audio/")) return "audio";
    if (file.type.startsWith("video/")) return "video";

    // Fallback to extension
    const extension = file.name.split(".").pop().toLowerCase();
    if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg", "tiff"].includes(extension)
    ) {
      return "image";
    } else if (["mp3", "wav", "m4a"].includes(extension)) {
      return "audio";
    } else if (["mp4"].includes(extension)) {
      return "video";
    }

    return "unknown";
  };

  // Function to handle file selection
  const handleFiles = (files) => {
    setError(null);

    // Convert FileList to array
    const fileArray = Array.from(files);

    // Validate files
    const invalidTypeFiles = fileArray.filter((file) => !isValidFileType(file));
    const invalidSizeFiles = fileArray.filter((file) => !isValidFileSize(file));

    if (invalidTypeFiles.length > 0) {
      setError(
        `File type is not supported. Supported types are ${fileExtensions.join(
          ", "
        )}`
      );
      return;
    }

    if (invalidSizeFiles.length > 0) {
      setError("File size is too big. Max size is 20MB");
      return;
    }

    // Limit the number of files if multiple is false
    let filesToProcess = fileArray;
    if (!multiple) {
      filesToProcess = [fileArray[0]];
    }

    // Create file objects with data URLs
    Promise.all(
      filesToProcess.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file: file,
                data_url: e.target.result,
                id: Date.now() + Math.random().toString(36).substring(2, 9),
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    )
      .then((newFiles) => {
        if (multiple) {
          // Add new files to existing array, but limit to 10 total (changed from 5)
          const updatedArray = [...imageArray, ...newFiles].slice(0, 10);
          setImageArray(updatedArray);

          // Check if max files warning is needed
          if (imageArray.length + newFiles.length > 10) {
            setError(
              `Maximum 10 files allowed. Only the first ${
                10 - imageArray.length
              } files were added.`
            );
          }
        } else {
          // Replace existing array with new file
          setImageArray(newFiles);
        }
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      })
      .catch((err) => {
        console.error("Error reading files:", err);
        setError("Error processing files. Please try again.");
      });
  };

  // Function to handle file input change
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  // Function to trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Function to remove a file
  const handleRemoveFile = (id) => {
    const updatedArray = imageArray.filter((img) => img.id !== id);
    setImageArray(updatedArray);
  };

  // Function to update a file
  const handleUpdateFile = (id) => {
    // Store the id of the file to update
    const updateIndex = imageArray.findIndex((img) => img.id === id);

    // Create a temporary input element
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptString;

    // Handle file selection
    input.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];

        // Validate file
        if (!isValidFileType(file)) {
          setError(
            `File type is not supported. Supported types are ${fileExtensions.join(
              ", "
            )}`
          );
          return;
        }

        if (!isValidFileSize(file)) {
          setError("File size is too big. Max size is 20MB");
          return;
        }

        // Read file as data URL
        const reader = new FileReader();
        reader.onload = (e) => {
          const updatedFile = {
            file: file,
            data_url: e.target.result,
            id: imageArray[updateIndex].id,
          };

          // Update array
          const updatedArray = [...imageArray];
          updatedArray[updateIndex] = updatedFile;
          setImageArray(updatedArray);
        };
        reader.readAsDataURL(file);
      }
    };

    // Trigger click
    input.click();
  };

  // Function to render file preview
  const renderFilePreview = (fileObj) => {
    const fileType = getFileType(fileObj.file);

    switch (fileType) {
      case "image":
        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "180px",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: darkMode
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.02)",
            }}
          >
            <img
              src={fileObj.data_url}
              alt=""
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        );
      case "audio":
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minHeight: "100px",
                borderRadius: "8px",
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
                p: 2,
              }}
            >
              <AudioFileIcon
                sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
              />
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-word", textAlign: "center" }}
              >
                {fileObj.file.name}
              </Typography>
            </Box>
            <audio
              controls
              src={fileObj.data_url}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "20px",
              }}
            />
          </Box>
        );
      case "video":
        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <video
              controls
              src={fileObj.data_url}
              style={{
                width: "100%",
                maxHeight: "180px",
              }}
            />
          </Box>
        );
      default:
        return (
          <Box
            sx={{
              backgroundColor: darkMode
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
              p: 3,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "180px",
            }}
          >
            <InsertDriveFileIcon
              sx={{ fontSize: 50, mb: 2, color: "primary.light" }}
            />
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              {fileObj.file.name || "Unknown file"}
            </Typography>
          </Box>
        );
    }
  };

  // Clear drag events if component unmounts while dragging
  useEffect(() => {
    return () => setDragActive(false);
  }, []);

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptString}
        multiple={multiple}
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {/* Error message */}
      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Main upload area */}
      <Paper
        ref={dropAreaRef}
        elevation={1}
        sx={{
          borderRadius: "12px",
          padding: "16px",
          backgroundColor: darkMode ? "#333" : "#f9f9f9",
          color: darkMode ? "#fff" : "#000",
          transition: "all 0.3s ease",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: "180px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 3,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "30%" },
              minHeight: "180px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: dragActive
                ? darkMode
                  ? "rgba(66, 165, 245, 0.1)"
                  : "rgba(66, 165, 245, 0.05)"
                : "transparent",
              border: "2px dashed",
              borderColor: dragActive
                ? "primary.main"
                : darkMode
                ? "#aaa"
                : "#888",
              borderRadius: "12px",
              padding: "20px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.02)",
                borderColor: "primary.main",
                transform: "translateY(-2px)",
              },
            }}
            onClick={handleUploadClick}
          >
            <AddPhotoAlternateIcon
              fontSize="large"
              color="primary"
              sx={{
                fontSize: 40,
                mb: 2,
                transition: "transform 0.3s ease",
                transform: dragActive ? "scale(1.2)" : "scale(1)",
              }}
            />
            <Typography variant="body1" align="center">
              {dragActive ? "Drop files here" : "Click or Drag to Upload"}
            </Typography>
            <Typography
              variant="caption"
              align="center"
              sx={{
                mt: 1,
                opacity: 0.7,
                maxWidth: "80%",
              }}
            >
              Supports images, audio, and video files
            </Typography>
          </Button>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 2,
              width: { xs: "100%", sm: "70%" },
            }}
          >
            {imageArray.length === 0 ? (
              <Box
                sx={{
                  width: "100%",
                  height: "180px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "12px",
                  backgroundColor: darkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.01)",
                  border: "1px dashed",
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.1)",
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  No files selected
                </Typography>
              </Box>
            ) : (
              imageArray.map((fileObj) => (
                <Paper
                  key={fileObj.id}
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "12px",
                    padding: "12px",
                    width: {
                      xs: "100%",
                      sm: "calc(50% - 16px)",
                      md: "calc(50% - 16px)",
                    },
                    backgroundColor: darkMode
                      ? "rgba(255,255,255,0.03)"
                      : "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  {renderFilePreview(fileObj)}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                      mt: 2,
                    }}
                  >
                    <IconButton
                      onClick={() => handleUpdateFile(fileObj.id)}
                      size="small"
                      color="primary"
                      sx={{
                        mr: 1,
                        backgroundColor: darkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.03)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: darkMode
                            ? "rgba(66, 165, 245, 0.15)"
                            : "rgba(66, 165, 245, 0.1)",
                        },
                      }}
                    >
                      <ModeEditOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRemoveFile(fileObj.id)}
                      size="small"
                      color="error"
                      sx={{
                        backgroundColor: darkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.03)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: darkMode
                            ? "rgba(244, 67, 54, 0.15)"
                            : "rgba(244, 67, 54, 0.1)",
                        },
                      }}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </Box>
      </Paper>

      {/* Info text */}
      <Typography
        variant="body2"
        sx={{ fontSize: "12px", marginTop: "12px", opacity: 0.7 }}
      >
        Upload Media {multiple ? "(Max: 10 files, " : "("}Max size: 20MB,
        Supported types: .jpg, .gif, .png, .webp, .jpeg, .mp3, .mp4, .wav, .svg,
        .tiff, .m4a)
      </Typography>
    </>
  );
}
